import {
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";
import { logout } from "../../services/login";
import { v } from "../../utils/runtime";

/**
 * Sidebar-навигация только для десктопной веб-версии.
 *
 * Заменяет `<Navigation />` (bottom bar) в веб-режиме на ширине >= md.
 * Ставится фиксированной колонкой слева, содержит:
 *   • логотип Zero (клик → /);
 *   • основные вкладки (Home / Cards / Exchange или Services / Profile);
 *   • разделитель + системные пункты (устройства, logout);
 *   • свёрнутое (icon-only) состояние на «средних» десктопах (< 1200px)
 *     и развёрнутое с подписями на широких.
 *
 * Layout из RootLayout выдаёт нам фикс. ширину; сам компонент — просто
 * содержимое этой колонки. Он не знает про main-content и не заботится
 * о своей позиции — за это отвечает `RootLayout`.
 */
// Ширина sidebar-«плитки». Sidebar живёт внутри общего центрированного
// контейнера (max-width 1500), поэтому его ширина «съедает» долю этого
// контейнера и НЕ прибавляется к нему сверху.
export const DESKTOP_SIDEBAR_WIDTH = 232;
export const DESKTOP_SIDEBAR_WIDTH_COMPACT = 72;
// Верхний отступ, на котором sidebar «прилипает» при скролле.
export const DESKTOP_SIDEBAR_STICKY_TOP = 20;

interface DesktopSidebarProps {
  /** Свёрнутый режим — только иконки. */
  compact?: boolean;
}

export function DesktopSidebar({ compact = false }: DesktopSidebarProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(["home", "profile"]);
  const { isFeatureEnabled } = useFeatureFlags();
  const exchangeEnabled = isFeatureEnabled("exchange");

  const { isHome, isExchange, isCards, isProfile } = useMemo(
    () => ({
      isHome:
        location.pathname === "/" ||
        location.pathname === "/deposit" ||
        location.pathname === "/withdraw" ||
        location.pathname.startsWith("/asset/"),
      isExchange: location.pathname.startsWith("/exchange"),
      isCards: location.pathname.startsWith("/cards"),
      isProfile:
        location.pathname === "/profile" ||
        location.pathname === "/personal-data" ||
        location.pathname === "/about" ||
        location.pathname === "/faq" ||
        location.pathname.startsWith("/referral"),
    }),
    [location.pathname],
  );

  const isHistory = location.pathname.startsWith("/history");

  const mainLinks = [
    {
      key: "home",
      label: t("home:tabs.home"),
      icon: "solar:wallet-linear",
      iconActive: "solar:wallet-bold-duotone",
      path: "/",
      selected: isHome,
    },
    {
      key: "cards",
      label: t("home:tabs.cards"),
      icon: "solar:card-linear",
      iconActive: "solar:card-bold-duotone",
      path: "/cards",
      selected: isCards,
    },
    {
      key: "exchange",
      label: exchangeEnabled
        ? t("home:tabs.exchange")
        : t("home:tabs.services"),
      icon: exchangeEnabled
        ? "solar:refresh-square-linear"
        : "solar:widget-linear",
      iconActive: exchangeEnabled
        ? "solar:refresh-square-bold-duotone"
        : "solar:widget-bold-duotone",
      path: "/exchange",
      selected: isExchange,
    },
    {
      key: "history",
      label: t("home:tabs.history", { defaultValue: "История" }),
      icon: "solar:clock-circle-linear",
      iconActive: "solar:clock-circle-bold-duotone",
      path: "/history",
      selected: isHistory,
    },
    {
      key: "profile",
      label: t("home:tabs.profile"),
      icon: "solar:user-linear",
      iconActive: "solar:user-bold-duotone",
      path: "/profile",
      selected: isProfile,
    },
  ];

  const [loggingOut, setLoggingOut] = useState(false);
  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      window.location.replace("/");
    }
  };

  const sidebarW = compact ? DESKTOP_SIDEBAR_WIDTH_COMPACT : DESKTOP_SIDEBAR_WIDTH;

  return (
    <Box
      sx={{
        width: sidebarW,
        flexShrink: 0,
        position: "sticky",
        top: DESKTOP_SIDEBAR_STICKY_TOP,
        alignSelf: "flex-start",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
      }}
    >
      {/* Логотип — над блоком sidebar, по центру */}
      <Box
        onClick={() => navigate("/")}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          py: 0.5,
        }}
      >
        <Box
          component="img"
          src={isDark ? v("/alfin-logo.svg") : v("/alfin-logo-l.svg")}
          alt="Zero Wallet"
          sx={{
            height: compact ? 28 : 24,
            width: "auto",
            maxWidth: "160px",
            objectFit: "contain",
          }}
        />
      </Box>

      {/* Блок sidebar */}
      <Box
        component="aside"
        sx={{
          width: "100%",
          maxHeight: `calc(100vh - ${DESKTOP_SIDEBAR_STICKY_TOP * 2 + 52}px)`,
          borderRadius: "24px",
          border: isDark ? "none" : "1px solid rgba(0,0,0,0.05)",
          bgcolor: "secondary.main",
          boxShadow: isDark
            ? "0 10px 40px rgba(0,0,0,0.35)"
            : "0 8px 32px rgba(15,15,18,0.06)",
          display: "flex",
          flexDirection: "column",
          py: 2.5,
          px: compact ? 1 : 2,
          zIndex: 50,
          overflow: "auto",
        }}
      >
      <Stack spacing={0.5}>
        {mainLinks.map((link) => (
          <NavItem
            key={link.key}
            icon={link.selected ? link.iconActive : link.icon}
            label={link.label}
            selected={link.selected}
            compact={compact}
            onClick={() => navigate(link.path)}
          />
        ))}
      </Stack>

      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ my: 1.5, opacity: 0.6 }} />

      <Stack spacing={0.5}>
        <NavItem
          icon="solar:devices-linear"
          label={t("profile:blocks.sessions")}
          selected={location.pathname === "/sessions"}
          compact={compact}
          onClick={() => navigate("/sessions")}
        />
        <NavItem
          icon="solar:logout-3-linear"
          label={t("profile:blocks.logout")}
          selected={false}
          compact={compact}
          disabled={loggingOut}
          onClick={handleLogout}
          danger
        />
      </Stack>
      </Box>
    </Box>
  );
}

// ---------- NavItem ----------

interface NavItemProps {
  icon: string;
  label: string;
  selected: boolean;
  compact: boolean;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

function NavItem({
  icon,
  label,
  selected,
  compact,
  onClick,
  disabled,
  danger,
}: NavItemProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const activeBg = isDark
    ? "rgba(51,119,255,0.18)"
    : "rgba(51,119,255,0.10)";
  const hoverBg = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)";

  const content = (
    <IconButton
      disableRipple
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: "100%",
        height: 44,
        px: compact ? 0 : 1.5,
        justifyContent: compact ? "center" : "flex-start",
        gap: compact ? 0 : 1.5,
        borderRadius: 2,
        color: selected
          ? theme.palette.primary.main
          : danger
            ? theme.palette.error.main
            : isDark
              ? "rgba(235,235,245,0.75)"
              : "rgba(0,0,0,0.72)",
        bgcolor: selected ? activeBg : "transparent",
        transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          bgcolor: selected ? activeBg : hoverBg,
        },
      }}
    >
      <Icon icon={icon} width={22} height={22} />
      {!compact && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: selected ? 600 : 500,
            fontSize: 14,
            letterSpacing: 0.1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      )}
    </IconButton>
  );

  // В compact-режиме иконки без подписей — навешиваем tooltip, чтобы
  // не было гадания «что это за иконка».
  if (compact) {
    return (
      <Tooltip title={label} placement="right" arrow>
        <Box>{content}</Box>
      </Tooltip>
    );
  }
  return content;
}
